import React, { useState, useEffect } from "react";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import moment from "moment-timezone";
import useTranslationHook from "../../hooks/useTranslationHook";
const GiveawayCard = ({ image, countdownData }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0,
  });
  const { t } = useTranslationHook();
  const calculateTimeLeft = () => {
    if (!countdownData) {
      console.log("No countdown data available");
      return;
    }

    // Parse the camp start date
    const startDate = moment.tz(
      countdownData.camp_start_date.date,
      countdownData.camp_start_date.timezone
    );

    // Calculate the next draw date by adding interval days
    const nextDrawDate = startDate.clone().add(countdownData.interval, "days");

    // Get current time in the same timezone
    const now = moment.tz(countdownData.camp_start_date.timezone);

    // Calculate difference
    let diff = nextDrawDate.diff(now);

    if (diff <= 0) {
      // If time has passed, calculate next cycle
      const daysPassed = now.diff(startDate, "days");
      const cycles = Math.floor(daysPassed / countdownData.interval) + 1;
      const nextCycleDate = startDate
        .clone()
        .add(cycles * countdownData.interval, "days");
      diff = nextCycleDate.diff(now);
    }

    if (diff > 0) {
      const duration = moment.duration(diff);
      const newTimeLeft = {
        days: Math.floor(duration.asDays()),
        hours: duration.hours(),
        minutes: duration.minutes(),
        seconds: duration.seconds(),
        totalSeconds: Math.floor(diff / 1000),
      };

      setTimeLeft(newTimeLeft);
    } else {
      setTimeLeft({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
      });
    }
  };

  useEffect(() => {
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [countdownData]);

  const renderTime = (dimension, timeValue) => (
    <div className="time-wrapper">
      <div className="time-value">{String(timeValue).padStart(2, "0")}</div>
    </div>
  );

  const circleSize = {
    desktop: 60,
    mobile: 45,
  };

  // Calculate progress percentages for each circle
  const getDaysProgress = () => {
    const totalDaysInCycle = countdownData?.interval || 30;
    const daysElapsed = totalDaysInCycle - timeLeft.days;
    return Math.max(0, Math.min(1, daysElapsed / totalDaysInCycle));
  };

  const getHoursProgress = () => {
    const totalHours = 24;
    const hoursElapsed = 24 - timeLeft.hours;
    return Math.max(0, Math.min(1, hoursElapsed / totalHours));
  };

  const getMinutesProgress = () => {
    const totalMinutes = 60;
    const minutesElapsed = 60 - timeLeft.minutes;
    return Math.max(0, Math.min(1, minutesElapsed / totalMinutes));
  };

  const getSecondsProgress = () => {
    const secondsElapsed = 60 - timeLeft.seconds;
    return Math.max(0, Math.min(1, secondsElapsed / 60));
  };

  return (
    <div className="giveaway-card-container">
      <div
        className="giveaway-card"
        style={{
          color: countdownData?.text_color || "#000",
        }}
      >
        <div className="left-section">
          <img src={image} alt="giveaway" className="giveaway-image" />
        </div>

        <div className="right-section">
          <div className="giveaway-countdown">
            <div className="countdown-circle">
              <CountdownCircleTimer
                key={`days-${timeLeft.days}`}
                isPlaying={false}
                duration={1}
                initialRemainingTime={1 - getDaysProgress()}
                colors={["#2a3a7d"]}
                size={
                  window.innerWidth <= 768
                    ? circleSize.mobile
                    : circleSize.desktop
                }
                strokeWidth={1.5}
                trailStrokeWidth={4}
                trailColor="#e0e0e0"
              >
                {() => renderTime("DAYS", timeLeft.days)}
              </CountdownCircleTimer>
              <div className="time-label">{t("days")}</div>
            </div>

            <div className="countdown-circle">
              <CountdownCircleTimer
                key={`hours-${timeLeft.hours}`}
                isPlaying={false}
                duration={1}
                initialRemainingTime={1 - getHoursProgress()}
                colors={["#2a3a7d"]}
                size={
                  window.innerWidth <= 768
                    ? circleSize.mobile
                    : circleSize.desktop
                }
                strokeWidth={1.5}
                trailStrokeWidth={4}
                trailColor="#e0e0e0"
              >
                {() => renderTime("HOURS", timeLeft.hours)}
              </CountdownCircleTimer>
              <div className="time-label">{t("hours")}</div>
            </div>

            <div className="countdown-circle">
              <CountdownCircleTimer
                key={`minutes-${timeLeft.minutes}`}
                isPlaying={false}
                duration={1}
                initialRemainingTime={1 - getMinutesProgress()}
                colors={["#2a3a7d"]}
                size={
                  window.innerWidth <= 768
                    ? circleSize.mobile
                    : circleSize.desktop
                }
                strokeWidth={1.5}
                trailStrokeWidth={4}
                trailColor="#e0e0e0"
              >
                {() => renderTime("MINUTES", timeLeft.minutes)}
              </CountdownCircleTimer>
              <div className="time-label">{t("minutes")}</div>
            </div>

            <div className="countdown-circle">
              <CountdownCircleTimer
                key={`seconds-${timeLeft.seconds}`}
                isPlaying={true}
                duration={60}
                initialRemainingTime={timeLeft.seconds}
                colors={["#2a3a7d"]}
                size={
                  window.innerWidth <= 768
                    ? circleSize.mobile
                    : circleSize.desktop
                }
                strokeWidth={1.5}
                trailStrokeWidth={4}
                trailColor="#e0e0e0"
                onComplete={() => ({ shouldRepeat: true, delay: 0 })}
              >
                {() => renderTime("SECONDS", timeLeft.seconds)}
              </CountdownCircleTimer>
              <div className="time-label">{t("seconds")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiveawayCard;
